import { useState } from "react";
import type { ApiHistory } from "../types";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, ArrowRight, ArrowDown } from "lucide-react";
import { parseChanges } from "../utils";
import { getActionVariant } from "../utils";
import { statusBadgeVariant } from "@/features/dilemmas/utils";
import frIcon from "@/assets/fr-icon.svg";
import enIcon from "@/assets/en-icon.svg";
import { textTruncate } from "@/utils/misc";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const T_PREFIX = "modules.history.changes_dialog";

const LANGUAGE_ICONS: Record<string, string> = {
  fr: frIcon,
  en: enIcon,
};

const HEX_COLOR_RE = /^#[0-9a-fA-F]{3,8}$/;
const BRACKET_LIST_RE = /^\[.*\]$/;
const KNOWN_STATUSES = new Set([
  "draft",
  "published",
  "archived",
  "scheduled",
  "active",
  "inactive",
]);
const DILEMMA_STATUSES = new Set([
  "draft",
  "published",
  "archived",
  "scheduled",
]);

function humanizeKey(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseBracketList(value: string): string[] {
  const trimmed = value.replace(/^\[|\]$/g, "");
  if (!trimmed) return [];
  return trimmed.split(",").map((s) => s.trim().replace(/^'|'$/g, ""));
}

function renderStatusBadge(value: string, t: TFunction) {
  if (DILEMMA_STATUSES.has(value)) {
    return (
      <Badge variant={statusBadgeVariant(value)}>
        {t(`modules.dilemmas.keys.status_${value}`)}
      </Badge>
    );
  }
  const isActive = value === "active";
  return (
    <Badge variant={isActive ? "success" : "destructive"}>
      {isActive ? t("keys.active") : t("keys.desactivated")}
    </Badge>
  );
}

function renderChangeValue(key: string, value: string, t: TFunction) {
  // 1. Empty / null
  if (value === "None" || value === "" || value === "null") {
    return (
      <span className="text-muted-foreground italic">
        {t(`${T_PREFIX}.no_value`)}
      </span>
    );
  }

  // 2. Status (key-specific — status values look like plain words)
  if (key === "status" && KNOWN_STATUSES.has(value)) {
    return renderStatusBadge(value, t);
  }

  // 3. Language (key-specific — short codes need key context)
  if (key === "default_language" && value in LANGUAGE_ICONS) {
    return (
      <div className="flex items-center gap-2">
        <img
          src={LANGUAGE_ICONS[value]}
          alt={value.toUpperCase()}
          className="size-5"
        />
        <span>{t(`languages.${value}`)}</span>
      </div>
    );
  }

  // 4. Hex color (value-detected)
  if (HEX_COLOR_RE.test(value)) {
    return (
      <div className="flex items-center gap-2">
        <span
          className="size-4 rounded-full border border-gray-200 shrink-0"
          style={{ backgroundColor: value }}
        />
        <span>{value}</span>
      </div>
    );
  }

  // 4. Boolean (value-detected)
  if (value === "True" || value === "False") {
    return (
      <span>
        {value === "True" ? t(`${T_PREFIX}.yes`) : t(`${T_PREFIX}.no`)}
      </span>
    );
  }

  // 5. Bracket list (value-detected)
  if (BRACKET_LIST_RE.test(value)) {
    const items = parseBracketList(value);
    return (
      <div className="flex gap-1 flex-wrap">
        {items.length === 0 ? (
          <span className="text-muted-foreground italic">
            {t(`${T_PREFIX}.no_value`)}
          </span>
        ) : (
          items.map((item) => (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge key={item} variant="secondary">
                  <span className="text-nowrap">{textTruncate(item, 25)}</span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>{item}</p>
              </TooltipContent>
            </Tooltip>
          ))
        )}
      </div>
    );
  }

  // 6. File path (key-specific — extract filename from path)
  if (key === "uploaded_files.uploaded_file") {
    const filename = value.split("/").pop() ?? value;
    return <span>{filename}</span>;
  }

  // 7. Fallback: plain text
  return <span>{value}</span>;
}

const ChangesContent = ({ history }: { history: ApiHistory }) => {
  const { t } = useTranslation();
  const changes = parseChanges(history.changes);

  return (
    <div className="overflow-y-auto max-h-[60vh] px-4 md:px-0 pb-6">
      <div className="flex flex-col md:flex-row items-center gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Badge variant="secondary">
            {t(`keys.resources.${history.model_type}`)}
          </Badge>
          <Badge variant={getActionVariant(history.action_display)}>
            {t(`keys.action_types.${history.action_display}`)}
          </Badge>
        </div>
        <span className="text-sm text-muted-foreground truncate">
          {history.object_repr}
        </span>
      </div>
      {changes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t(`${T_PREFIX}.no_value`)}
        </p>
      ) : (
        <ul className="space-y-3">
          {changes.map((change) => {
            const labelKey = change.key.split(".")[0];
            const fieldLabel =
              t(`${T_PREFIX}.fields.${labelKey}`, {
                defaultValue: "",
              }) || humanizeKey(labelKey);

            return (
              <li key={change.key} className="rounded-md border p-3 text-sm">
                <p className="font-medium mb-2">{fieldLabel}</p>
                <div className="flex flex-col sm:flex-row items-start gap-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs mb-0.5 text-red-800">
                      {t(`${T_PREFIX}.old_value`)}
                    </p>
                    <div className="break-all">
                      {renderChangeValue(change.key, change.oldValue, t)}
                    </div>
                  </div>
                  <ArrowDown className="sm:hidden size-4 shrink-0 text-muted-foreground" />
                  <ArrowRight className="hidden sm:block size-4 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs mb-0.5 text-green-800">
                      {t(`${T_PREFIX}.new_value`)}
                    </p>
                    <div className="break-all">
                      {renderChangeValue(change.key, change.newValue, t)}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

interface ChangesDialogProps {
  history: ApiHistory;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ChangesDialog = ({
  history,
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: ChangesDialogProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const onOpenChange = isControlled ? controlledOnOpenChange! : setInternalOpen;

  const defaultTrigger = (
    <Button size="xs" variant="outline">
      <Eye />
      {t("keys.see_changes")}
    </Button>
  );

  const dialogTitle = t(`${T_PREFIX}.title`);
  const closeLabel = t(`${T_PREFIX}.close`);

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        {trigger !== undefined ? (
          trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        ) : (
          <DrawerTrigger asChild>{defaultTrigger}</DrawerTrigger>
        )}
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>{dialogTitle}</DrawerTitle>
            <DrawerDescription className="sr-only">
              {dialogTitle}
            </DrawerDescription>
          </DrawerHeader>
          <ChangesContent history={history} />
          <DrawerFooter className="pt-2">
            <DrawerClose asChild>
              <Button variant="outline">{closeLabel}</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger !== undefined ? (
        trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : (
        <DialogTrigger asChild>{defaultTrigger}</DialogTrigger>
      )}
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription className="sr-only">
            {dialogTitle}
          </DialogDescription>
        </DialogHeader>
        <ChangesContent history={history} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{closeLabel}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangesDialog;
